import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  changeAdmin,
  createAdminTenant,
  createAdminType,
  deleteAdminType,
  deleteAdminUser,
  getAdminTenants,
  getAdminUsers,
  getTypeCatalog,
  renameTenant,
  setAdminTenantStatus,
  setAdminUserDeactivated,
  updateAdminType,
  type AdminTenant,
  type AdminUser,
  type TypeCatalogEntry,
} from './api';
import type { Translate } from './i18n';
import { tenantLabel } from './tenant';
import { ActionRow, Field, FieldGrid, Section } from './ui';

function dateLabel(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

interface TypeDraft {
  id: string | null;
  source: string;
  value: string;
  labelEn: string;
  labelSv: string;
  descriptionEn: string;
  descriptionSv: string;
  sort: string;
}

const EMPTY_TYPE: TypeDraft = {
  id: null,
  source: 'WeHunt',
  value: '',
  labelEn: '',
  labelSv: '',
  descriptionEn: '',
  descriptionSv: '',
  sort: '0',
};

function draftFromType(entry: TypeCatalogEntry): TypeDraft {
  return {
    id: entry.id,
    source: entry.source,
    value: entry.value,
    labelEn: entry.labelEn ?? '',
    labelSv: entry.labelSv ?? '',
    descriptionEn: entry.descriptionEn ?? '',
    descriptionSv: entry.descriptionSv ?? '',
    sort: String(entry.sort),
  };
}

/** Platform operator surface: tenant lifecycle, user directory and global type catalog. */
export function AdminPage({ t, onClose }: { t: Translate; onClose: () => void }) {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [types, setTypes] = useState<TypeCatalogEntry[]>([]);
  const [typeDraft, setTypeDraft] = useState<TypeDraft>(EMPTY_TYPE);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [newName, setNewName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [links, setLinks] = useState<Record<string, string>>({});
  const [adminEmails, setAdminEmails] = useState<Record<string, string>>({});
  const [tenantNames, setTenantNames] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    const [tenantResult, userResult, typeResult] = await Promise.all([
      getAdminTenants(),
      getAdminUsers(includeInactive),
      getTypeCatalog(),
    ]);
    setTenants(tenantResult.tenants);
    setUsers(userResult.users);
    setTypes(typeResult.types);
  }, [includeInactive]);

  useEffect(() => {
    void load().catch((err) => setMessage(String(err)));
  }, [load]);

  const visibleTenants = useMemo(
    () => tenants.filter((tenant) => includeArchived || tenant.status !== 'archived'),
    [tenants, includeArchived],
  );

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setMessage('');
    try {
      await action();
      await load();
    } catch (err) {
      setMessage(String(err));
    } finally {
      setBusy(false);
    }
  };

  const onCreateTenant = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newName.trim() || !newAdminEmail.trim()) return;
    await run(async () => {
      const result = await createAdminTenant(newName.trim(), newAdminEmail.trim());
      setLinks((current) => ({ ...current, [result.tenantId]: result.link }));
      setNewName('');
      setNewAdminEmail('');
      setMessage(
        result.emailQueued ? t('admin.tenantCreatedEmail') : t('admin.tenantCreated'),
      );
    });
  };

  const onStatus = (tenant: AdminTenant, status: 'active' | 'suspended' | 'archived') =>
    run(async () => {
      await setAdminTenantStatus(tenant.id, status);
      setMessage(t('admin.tenantUpdated'));
    });

  const onRenameTenant = (tenant: AdminTenant) => {
    const name = (tenantNames[tenant.id] ?? tenant.name).trim();
    if (!name || name === tenant.name) return;
    return run(async () => {
      await renameTenant(tenant.id, name);
      setTenantNames((current) => {
        const copy = { ...current };
        delete copy[tenant.id];
        return copy;
      });
      setMessage(t('admin.tenantRenamed'));
    });
  };

  const onSaveType = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!typeDraft.source.trim() || !typeDraft.value.trim()) return;
    await run(async () => {
      const input = {
        source: typeDraft.source.trim(),
        value: typeDraft.value.trim(),
        labelSv: typeDraft.labelSv.trim() || null,
        labelEn: typeDraft.labelEn.trim() || null,
        descriptionSv: typeDraft.descriptionSv.trim() || null,
        descriptionEn: typeDraft.descriptionEn.trim() || null,
        sort: Number(typeDraft.sort) || 0,
      };
      try {
        if (typeDraft.id) {
          await updateAdminType(typeDraft.id, input);
          setMessage(t('admin.typeUpdated'));
        } else {
          await createAdminType(input);
          setMessage(t('admin.typeCreated'));
        }
        setTypeDraft((current) => ({
          ...EMPTY_TYPE,
          source: current.source,
          sort: current.sort,
        }));
      } catch (err) {
        setMessage(`${t('admin.typeSaveFailed')} ${String(err)}`);
      }
    });
  };

  const onDeleteType = (entry: TypeCatalogEntry) => {
    if (!window.confirm(t('admin.typeDeleteConfirm', { value: entry.value, source: entry.source }))) {
      return;
    }
    void run(async () => {
      await deleteAdminType(entry.id);
      setMessage(t('admin.typeDeleted'));
      if (typeDraft.id === entry.id) setTypeDraft(EMPTY_TYPE);
    });
  };

  const onChangeAdmin = (tenant: AdminTenant) => {
    const email = (adminEmails[tenant.id] ?? '').trim();
    if (!email) return;
    return run(async () => {
      const result = await changeAdmin(tenant.id, email);
      setLinks((current) => ({ ...current, [tenant.id]: result.link }));
      setAdminEmails((current) => ({ ...current, [tenant.id]: '' }));
      setMessage(
        result.emailQueued ? t('admin.adminChangedEmail') : t('admin.adminChanged'),
      );
    });
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <div>
          <h1>{t('admin.title')}</h1>
          <p className="muted">{t('admin.hint')}</p>
        </div>
        <button className="button" onClick={onClose}>{t('admin.back')}</button>
      </div>

      {message && <div className="warning attention">{message}</div>}

      <Section title={t('admin.newTenant')}>
        <form className="ui-form admin-form" onSubmit={onCreateTenant}>
          <FieldGrid>
            <Field label={t('admin.tenantName')}>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} />
            </Field>
            <Field label={t('admin.firstAdminEmail')}>
              <input type="email" value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} />
            </Field>
          </FieldGrid>
          <ActionRow>
            <button
              type="submit"
              className="button primary"
              disabled={busy || !newName.trim() || !newAdminEmail.trim()}
            >
              {t('admin.createTenant')}
            </button>
          </ActionRow>
        </form>
      </Section>

      <Section
        title={t('admin.tenants')}
        actions={
          <label className="inline-check">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t('admin.showArchived')}
          </label>
        }
      >
        {visibleTenants.length === 0 && <p className="muted">{t('admin.noTenants')}</p>}
        <ul className="admin-list">
          {visibleTenants.map((tenant) => (
            <li key={tenant.id} className="admin-row">
              <div className="admin-row-main">
                <strong>{tenantLabel(tenant.name, tenant.id)}</strong>
                <span className={`badge ${tenant.status === 'active' ? 'approved' : tenant.status === 'archived' ? 'aborted' : 'rejected'}`}>
                  {t(`admin.status.${tenant.status}`)}
                </span>
                <span className="muted">
                  {t('admin.tenantMeta', {
                    members: tenant.memberCount,
                    maps: tenant.mapCount,
                    admin: tenant.adminEmail ?? t('admin.noAdmin'),
                  })}
                </span>
              </div>
              <div className="toolbar">
                <input
                  aria-label={t('admin.tenantName')}
                  value={tenantNames[tenant.id] ?? tenant.name}
                  onChange={(event) =>
                    setTenantNames((current) => ({ ...current, [tenant.id]: event.target.value }))
                  }
                />
                <button
                  className="button"
                  disabled={
                    busy ||
                    !(tenantNames[tenant.id] ?? tenant.name).trim() ||
                    (tenantNames[tenant.id] ?? tenant.name).trim() === tenant.name
                  }
                  onClick={() => void onRenameTenant(tenant)}
                >
                  {t('admin.renameTenant')}
                </button>
                {tenant.status === 'active' && (
                  <>
                    <button className="button" disabled={busy} onClick={() => void onStatus(tenant, 'suspended')}>
                      {t('admin.suspend')}
                    </button>
                    <button className="button danger" disabled={busy} onClick={() => void onStatus(tenant, 'archived')}>
                      {t('admin.archive')}
                    </button>
                  </>
                )}
                {tenant.status === 'suspended' && (
                  <>
                    <button className="button" disabled={busy} onClick={() => void onStatus(tenant, 'active')}>
                      {t('admin.restore')}
                    </button>
                    <button className="button danger" disabled={busy} onClick={() => void onStatus(tenant, 'archived')}>
                      {t('admin.archive')}
                    </button>
                  </>
                )}
                {tenant.status === 'archived' && (
                  <button className="button" disabled={busy} onClick={() => void onStatus(tenant, 'active')}>
                    {t('admin.restore')}
                  </button>
                )}
                <input
                  type="email"
                  placeholder={t('admin.changeAdminEmail')}
                  value={adminEmails[tenant.id] ?? ''}
                  onChange={(e) =>
                    setAdminEmails((current) => ({ ...current, [tenant.id]: e.target.value }))
                  }
                />
                <button
                  className="button"
                  disabled={busy || !(adminEmails[tenant.id] ?? '').trim()}
                  onClick={() => void onChangeAdmin(tenant)}
                >
                  {t('admin.changeAdmin')}
                </button>
              </div>
              {links[tenant.id] && (
                <Field label={t('admin.link')}>
                  <input readOnly value={links[tenant.id]} onFocus={(e) => e.currentTarget.select()} />
                </Field>
              )}
            </li>
          ))}
        </ul>
      </Section>

      <Section
        title={t('admin.users')}
        actions={
          <label className="inline-check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {t('admin.showInactive')}
          </label>
        }
      >
        {users.length === 0 && <p className="muted">{t('admin.noUsers')}</p>}
        <ul className="admin-list">
          {users.map((user) => (
            <li key={user.id} className={`admin-row${user.deactivatedAt ? ' inactive' : ''}`}>
              <div className="admin-row-main">
                <strong>{user.email}</strong>
                {user.isPlatformAdmin && <span className="badge approved">{t('admin.platformAdmin')}</span>}
                {user.deactivatedAt && <span className="badge aborted">{t('admin.inactive')}</span>}
                <span className="muted">
                  {t('admin.lastLogin')}: {dateLabel(user.lastLoginAt)}
                </span>
              </div>
              <div className="admin-memberships">
                {user.memberships.length === 0 && <span className="muted">{t('admin.noMemberships')}</span>}
                {user.memberships.map((membership) => (
                  <span key={membership.tenantId} className="chip">
                    {tenantLabel(membership.tenantName, membership.tenantId)}:{' '}
                    {t(`role.${membership.role}`)}
                  </span>
                ))}
              </div>
              <div className="toolbar">
                {user.isPlatformAdmin ? null : (
                  <>
                    <button
                      className="button"
                      disabled={busy}
                      onClick={() =>
                        void run(async () => {
                          await setAdminUserDeactivated(user.id, !user.deactivatedAt);
                          setMessage(t('admin.userUpdated'));
                        })
                      }
                    >
                      {user.deactivatedAt ? t('admin.reactivate') : t('admin.deactivate')}
                    </button>
                    <button
                      className="button danger"
                      disabled={busy}
                      onClick={() => {
                        if (!window.confirm(t('admin.deleteUserConfirm', { email: user.email }))) return;
                        void run(async () => {
                          await deleteAdminUser(user.id);
                          setMessage(t('admin.userDeleted'));
                        });
                      }}
                    >
                      {t('admin.deleteUser')}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t('admin.typeCatalog')} description={t('admin.typeCatalogHint')}>
        <form className="ui-form" onSubmit={(event) => void onSaveType(event)}>
          <FieldGrid>
            <Field label={t('admin.typeSource')}>
              <input
                value={typeDraft.source}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, source: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeValue')}>
              <input
                value={typeDraft.value}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, value: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeLabelEn')}>
              <input
                value={typeDraft.labelEn}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, labelEn: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeLabelSv')}>
              <input
                value={typeDraft.labelSv}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, labelSv: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeDescriptionEn')}>
              <input
                value={typeDraft.descriptionEn}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, descriptionEn: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeDescriptionSv')}>
              <input
                value={typeDraft.descriptionSv}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, descriptionSv: event.target.value }))
                }
              />
            </Field>
            <Field label={t('admin.typeSort')}>
              <input
                type="number"
                value={typeDraft.sort}
                onChange={(event) =>
                  setTypeDraft((current) => ({ ...current, sort: event.target.value }))
                }
              />
            </Field>
          </FieldGrid>
          <ActionRow>
            <button
              type="submit"
              className="button primary"
              disabled={busy || !typeDraft.source.trim() || !typeDraft.value.trim()}
            >
              {typeDraft.id ? t('admin.typeSave') : t('admin.typeAdd')}
            </button>
            {typeDraft.id && (
              <button type="button" className="button" onClick={() => setTypeDraft(EMPTY_TYPE)}>
                {t('admin.typeCancel')}
              </button>
            )}
          </ActionRow>
        </form>

        {types.length === 0 && <p className="muted">{t('admin.typeEmpty')}</p>}
        <ul className="admin-list">
          {types.map((entry) => (
            <li key={entry.id} className="admin-row">
              <div className="admin-row-main">
                <strong>{entry.value}</strong>
                <span className="chip">{entry.source}</span>
                <span className="muted">
                  {entry.labelEn || '—'} / {entry.labelSv || '—'}
                </span>
              </div>
              <div className="toolbar">
                <button className="button" disabled={busy} onClick={() => setTypeDraft(draftFromType(entry))}>
                  {t('admin.typeEdit')}
                </button>
                <button
                  className="button danger"
                  disabled={busy}
                  onClick={() => onDeleteType(entry)}
                >
                  {t('admin.typeDelete')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
